import React, { useState } from 'react';
import { formatKlst } from '@/utils/formatHours';
import {
  ArrowLeft, Briefcase, Clock, Receipt, BookOpen,
  Calendar, Users, TrendingUp, TrendingDown, MousePointerClick
} from 'lucide-react';

// Icelandic grouping is a dot. Some browser builds ship no is-IS locale data
// and silently fall back to commas, so group explicitly instead of trusting Intl.
let DEMO_LANG = 'is';
const ISK = (n) => {
  const s = Math.round(n || 0).toLocaleString('en-US');
  return DEMO_LANG === 'is' ? s.replace(/,/g, '.') : s;
};

// ── Demo data ──
// Every number reconciles: internal labour rate is 7.500 kr/klst on every
// project, receipt net sums equal the material cost, VAT is 24% of net, and
// the counts shown on Yfirlit match the tab contents.
const INTERNAL_RATE = 7500;
const DEMO_COMPANY = { name: 'GV Framkvæmdir ehf.', employees: 4 };

const DEMO_PROJECTS = [
  {
    id: 'p1',
    name: 'Baðherbergi - Ásvallagata 12',
    client: 'Sigríður Jónsdóttir',
    status: 'active',
    billing_type: 'fixed',
    fixed_price: 2400000,
    stats: { open_jobs: 2, diary_count: 6 },
    staff: [
      { name: 'Gísli Viðarsson', role: 'Verkstjóri' },
      { name: 'Aron Þórðarson', role: 'Iðnmaður' },
    ],
    // net 248.000 + 112.000 + 196.000 + 134.000 = 690.000
    receipts: [
      { id: 'r1', store: 'Byko Grafarvogur', net: 248000, vat: 59520, date: '22.3.2026' },
      { id: 'r2', store: 'Húsasmiðjan Skemmuvegi', net: 112000, vat: 26880, date: '21.3.2026' },
      { id: 'r3', store: 'Lagnaefni ehf.', net: 196000, vat: 47040, date: '20.3.2026' },
      { id: 'r4', store: 'Flísabúðin', net: 134000, vat: 32160, date: '18.3.2026' },
    ],
    receiptsCount: 4,
    // 6 visible x 8 klst = 48, plus 14 earlier entries = 112. Samtals 160.
    hours: [
      { worker: 'Gísli', hours: 8, date: '22.3.2026', start: '07:30', end: '16:00' },
      { worker: 'Aron', hours: 8, date: '22.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Gísli', hours: 8, date: '21.3.2026', start: '07:30', end: '16:00' },
      { worker: 'Aron', hours: 8, date: '21.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Gísli', hours: 8, date: '20.3.2026', start: '07:30', end: '16:00' },
      { worker: 'Aron', hours: 8, date: '20.3.2026', start: '08:00', end: '16:30' },
    ],
    earlierHours: { entries: 14, hours: 112 },
    hoursCount: 20,
    finance: { totalHours: 160, materialNet: 690000, materialVat: 165600, manualCosts: 45000 },
  },
  {
    id: 'p2',
    name: 'Eldhús - Bárugata 7',
    client: 'Magnús Ólafsson',
    status: 'active',
    billing_type: 'hourly',
    hourly_rate: 12500,
    stats: { open_jobs: 1, diary_count: 3 },
    staff: [
      { name: 'Aron Þórðarson', role: 'Iðnmaður' },
      { name: 'Kristján Sigurðsson', role: 'Iðnmaður' },
    ],
    // net 265.000 + 145.000 = 410.000
    receipts: [
      { id: 'r5', store: 'IKEA Kauptúni', net: 265000, vat: 63600, date: '21.3.2026' },
      { id: 'r6', store: 'Byko Grafarvogur', net: 145000, vat: 34800, date: '19.3.2026' },
    ],
    receiptsCount: 2,
    // 8 x 8 klst = 64. Samtals 64, engar fyrri skráningar.
    hours: [
      { worker: 'Aron', hours: 8, date: '22.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '22.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Aron', hours: 8, date: '21.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '21.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Aron', hours: 8, date: '20.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '20.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Aron', hours: 8, date: '19.3.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '19.3.2026', start: '08:00', end: '16:30' },
    ],
    hoursCount: 8,
    finance: { totalHours: 64, materialNet: 410000, materialVat: 98400, manualCosts: 0 },
  },
  {
    id: 'p3',
    name: 'Viðbygging - Sólvallagata 22',
    client: 'Haraldur Björnsson',
    status: 'finished',
    billing_type: 'fixed',
    fixed_price: 9800000,
    stats: { open_jobs: 0, diary_count: 18 },
    staff: [
      { name: 'Gísli Viðarsson', role: 'Verkstjóri' },
      { name: 'Aron Þórðarson', role: 'Iðnmaður' },
      { name: 'Kristján Sigurðsson', role: 'Iðnmaður' },
    ],
    // visible net 3.670.000 + 1.230.000 earlier = 4.900.000
    receipts: [
      { id: 'r7', store: 'Húsasmiðjan Skútuvogi', net: 1240000, vat: 297600, date: '14.2.2026' },
      { id: 'r8', store: 'Steypustöðin', net: 1150000, vat: 276000, date: '7.2.2026' },
      { id: 'r9', store: 'Byko Breidd', net: 860000, vat: 206400, date: '28.1.2026' },
      { id: 'r10', store: 'Rafkaup', net: 420000, vat: 100800, date: '21.1.2026' },
    ],
    earlierReceipts: { entries: 18, net: 1230000, vat: 295200 },
    receiptsCount: 22,
    // 6 visible x 8 klst = 48, plus 72 earlier entries = 572. Samtals 620.
    hours: [
      { worker: 'Gísli', hours: 8, date: '14.2.2026', start: '07:30', end: '16:00' },
      { worker: 'Aron', hours: 8, date: '14.2.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '14.2.2026', start: '08:00', end: '16:30' },
      { worker: 'Gísli', hours: 8, date: '13.2.2026', start: '07:30', end: '16:00' },
      { worker: 'Aron', hours: 8, date: '13.2.2026', start: '08:00', end: '16:30' },
      { worker: 'Kristján', hours: 8, date: '13.2.2026', start: '08:00', end: '16:30' },
    ],
    earlierHours: { entries: 72, hours: 572 },
    hoursCount: 78,
    finance: { totalHours: 620, materialNet: 4900000, materialVat: 1176000, manualCosts: 450000 },
    lesson: {
      is: 'Svona verk sérðu á miðri leið, ekki eftir á.',
      en: 'You see a job like this halfway through, not afterwards.',
    },
  },
];

// Derived economics. Single source of truth for every view.
const econ = (p) => {
  const laborCost = p.finance.totalHours * INTERNAL_RATE;
  const totalCost = laborCost + p.finance.materialNet + p.finance.manualCosts;
  const billable = p.billing_type === 'hourly'
    ? p.finance.totalHours * p.hourly_rate + p.finance.materialNet
    : 0;
  const margin = p.billing_type === 'fixed' ? p.fixed_price - totalCost : 0;
  const marginPct = p.billing_type === 'fixed' && p.fixed_price
    ? Math.round((margin / p.fixed_price) * 100)
    : 0;
  return { laborCost, totalCost, billable, margin, marginPct };
};

// ── Shared styles ──
const S = {
  bg: '#0B0B0C',
  card: '#151A20',
  cardHover: '#1A2028',
  border: '#1E2630',
  borderLight: '#2A3138',
  green: '#22C55E',
  greenSoft: 'rgba(34,197,94,0.1)',
  red: '#EF4444',
  text: '#F1F5F9',
  textMuted: '#9CA3AF',
  textDim: '#6B7280',
};

// ── Shared mini components ──
const Badge = ({ children, variant = 'success' }) => {
  const colors = {
    success: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E', border: 'rgba(34,197,94,0.2)' },
    neutral: { bg: 'rgba(156,163,175,0.1)', color: '#9CA3AF', border: 'rgba(156,163,175,0.2)' },
  };
  const c = colors[variant] || colors.neutral;
  return (
    <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {children}
    </span>
  );
};

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="p-3 rounded-lg text-center" style={{ background: S.card, border: `1px solid ${S.border}` }}>
    <Icon size={14} className="mx-auto mb-1" style={{ color: S.textDim }} />
    <p className="text-base sm:text-lg font-bold font-mono">{value}</p>
    <p className="text-[10px] sm:text-xs" style={{ color: S.textDim }}>{label}</p>
  </div>
);

const Card = ({ children, className = '', style = {} }) => (
  <div className={`rounded-lg ${className}`} style={{ background: S.card, border: `1px solid ${S.border}`, ...style }}>
    {children}
  </div>
);

const Row = ({ label, sub, value, color, strong, border }) => (
  <div className={`flex justify-between ${border ? 'pt-2' : ''} ${strong ? 'font-semibold' : ''}`}
    style={border ? { borderTop: `1px solid ${S.border}` } : {}}>
    <span style={{ color: strong ? S.text : S.textDim }}>
      {label}
      {sub && <span className="block text-[10px] font-mono" style={{ color: S.textDim }}>{sub}</span>}
    </span>
    <span className="font-mono flex-shrink-0" style={color ? { color } : {}}>{value}</span>
  </div>
);

// ── Project list view ──
const ProjectListView = ({ onSelect, lang, pulse }) => (
  <div className="space-y-2 p-3 sm:p-4">
    {DEMO_PROJECTS.map((p, i) => {
      const e = econ(p);
      const isHourly = p.billing_type === 'hourly';
      return (
        <button
          key={p.id}
          onClick={() => onSelect(p)}
          className={`w-full text-left rounded-lg p-3 sm:p-3.5 transition-colors hover:brightness-110 ${pulse && i === 0 ? 'demo-pulse' : ''}`}
          style={{ background: S.card, border: `1px solid ${pulse && i === 0 ? 'rgba(34,197,94,0.45)' : S.border}` }}
          data-testid={`demo-project-${p.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-semibold truncate">{p.name}</p>
                <Badge variant={p.status === 'active' ? 'success' : 'neutral'}>
                  {p.status === 'active' ? (lang === 'is' ? 'Virkt' : 'Active') : (lang === 'is' ? 'Lokið' : 'Finished')}
                </Badge>
              </div>
              <p className="text-[11px]" style={{ color: S.textDim }}>{p.client}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-mono font-medium">{ISK(isHourly ? e.billable : e.totalCost)} kr</p>
              <p className="text-[10px]" style={{ color: S.textDim }}>
                {isHourly
                  ? (lang === 'is' ? 'til að rukka' : 'to invoice')
                  : (lang === 'is' ? 'kostnaður' : 'cost')}
                {' '}&middot; {p.finance.totalHours} {lang === 'is' ? 'klst' : 'hrs'}
              </p>
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

// ── Overview tab ──
const OverviewTab = ({ project, lang }) => {
  const e = econ(project);
  const isFixed = project.billing_type === 'fixed';
  const positive = e.margin >= 0;
  return (
    <div className="space-y-3 p-3 sm:p-4">
      <div className="grid grid-cols-4 gap-2">
        <StatCard icon={Briefcase} value={project.stats.open_jobs} label={lang === 'is' ? 'Opin verk' : 'Open jobs'} />
        <StatCard icon={Clock} value={formatKlst(project.finance.totalHours)} label={lang === 'is' ? 'Tímar' : 'Hours'} />
        <StatCard icon={Receipt} value={project.receiptsCount} label={lang === 'is' ? 'Kvittanir' : 'Receipts'} />
        <StatCard icon={BookOpen} value={project.stats.diary_count} label={lang === 'is' ? 'Dagbók' : 'Diary'} />
      </div>

      <Card className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users size={14} style={{ color: S.textDim }} />
          <h3 className="text-xs sm:text-sm font-semibold">{lang === 'is' ? 'Starfsmenn' : 'Staff'}</h3>
        </div>
        <div className="space-y-2">
          {project.staff.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-medium flex-shrink-0" style={{ background: S.greenSoft, color: S.green }}>
                {s.name.charAt(0)}
              </div>
              <span className="truncate text-xs sm:text-sm">{s.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto flex-shrink-0" style={{ background: S.cardHover, color: S.textDim }}>{s.role}</span>
            </div>
          ))}
        </div>
      </Card>

      {isFixed ? (
        <Card className="p-3 sm:p-4" data-testid="demo-overview-result">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: positive ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)' }}>
              {positive
                ? <TrendingUp size={16} style={{ color: S.green }} />
                : <TrendingDown size={16} style={{ color: S.red }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold font-mono" style={{ color: positive ? S.green : S.red }}>
                {positive ? '+' : ''}{ISK(e.margin)} kr <span className="font-normal">({e.marginPct}%)</span>
              </p>
              <p className="text-[10px] sm:text-xs" style={{ color: S.textDim }}>{lang === 'is' ? 'Niðurstaða verks' : 'Project result'}</p>
            </div>
          </div>
          {project.lesson && (
            <p className="text-[10px] sm:text-xs mt-2.5 pt-2.5" style={{ color: S.textMuted, borderTop: `1px solid ${S.border}` }}>
              {project.lesson[lang === 'is' ? 'is' : 'en']}
            </p>
          )}
        </Card>
      ) : (
        <Card className="p-3 sm:p-4" data-testid="demo-overview-billable">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: S.greenSoft }}>
              <Receipt size={16} style={{ color: S.green }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold font-mono" style={{ color: S.green }}>{ISK(e.billable)} kr</p>
              <p className="text-[10px] sm:text-xs" style={{ color: S.textDim }}>
                {lang === 'is' ? 'Tilbúið til reikningsfærslu (án VSK)' : 'Ready to invoice (ex VAT)'}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// ── Receipts tab ──
const ReceiptsTab = ({ project, lang }) => {
  const earlier = project.earlierReceipts;
  return (
    <div className="space-y-2 p-3 sm:p-4">
      {project.receipts.map((r) => (
        <div key={r.id} className="rounded-lg p-3 sm:p-3.5" style={{ background: S.card, border: `1px solid ${S.border}` }}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{r.store}</p>
              <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: S.textDim }}>{r.date}</p>
            </div>
          </div>
          <div className="space-y-1 text-xs">
            <Row label={lang === 'is' ? 'Án VSK' : 'Ex VAT'} value={`${ISK(r.net)} kr`} strong />
            <Row label="VSK 24%" value={`${ISK(r.vat)} kr`} />
            <Row label={lang === 'is' ? 'Greitt' : 'Paid'} value={`${ISK(r.net + r.vat)} kr`} border />
          </div>
        </div>
      ))}
      {earlier && (
        <div className="rounded-lg p-3 text-xs" style={{ background: S.card, border: `1px dashed ${S.borderLight}` }}>
          <Row
            label={lang === 'is' ? `${earlier.entries} fyrri kvittanir` : `${earlier.entries} earlier receipts`}
            value={`${ISK(earlier.net)} kr`}
          />
        </div>
      )}
      <div className="flex justify-between pt-2 text-sm font-semibold" style={{ borderTop: `2px solid rgba(34,197,94,0.2)` }}>
        <span>{lang === 'is' ? 'Samtals án VSK' : 'Total ex VAT'}</span>
        <span className="font-mono" style={{ color: S.green }}>{ISK(project.finance.materialNet)} kr</span>
      </div>
    </div>
  );
};

// ── Hours tab ──
const HoursTab = ({ project, lang }) => {
  const earlier = project.earlierHours;
  return (
    <div className="p-3 sm:p-4">
      <div className="space-y-0">
        {project.hours.map((h, i) => (
          <div key={i} className="flex items-center gap-3 py-3" style={{ borderBottom: `1px solid ${S.border}` }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: S.greenSoft }}>
              <Clock size={16} style={{ color: S.green }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{h.worker}</p>
              <p className="text-[10px] sm:text-xs flex items-center gap-1" style={{ color: S.textDim }}>
                <Calendar size={10} />{h.date}
                {h.start && h.end && <span className="ml-1 font-mono">{h.start} - {h.end}</span>}
              </p>
            </div>
            <p className="text-base sm:text-lg font-mono font-semibold flex-shrink-0">{formatKlst(h.hours)}</p>
          </div>
        ))}
        {earlier && (
          <div className="flex items-center justify-between py-3 text-xs" style={{ borderBottom: `1px dashed ${S.borderLight}`, color: S.textDim }}>
            <span>{lang === 'is' ? `${earlier.entries} fyrri skráningar` : `${earlier.entries} earlier entries`}</span>
            <span className="font-mono">{formatKlst(earlier.hours)}</span>
          </div>
        )}
        <div className="flex items-center justify-between py-3" style={{ borderTop: `2px solid rgba(34,197,94,0.2)` }}>
          <span className="text-sm font-medium">{lang === 'is' ? 'Samtals' : 'Total'}</span>
          <span className="text-lg sm:text-xl font-mono font-semibold" style={{ color: S.green }}>{formatKlst(project.finance.totalHours)}</span>
        </div>
      </div>
    </div>
  );
};

// ── Finance tab ──
const FinanceTab = ({ project, lang }) => {
  const f = project.finance;
  const e = econ(project);
  const isFixed = project.billing_type === 'fixed';
  const positive = e.margin >= 0;

  return (
    <div className="space-y-3 p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-2">
        <Card className="p-3 text-center">
          <p className="text-lg sm:text-xl font-bold font-mono" style={{ color: S.green }}>{formatKlst(f.totalHours)}</p>
          <p className="text-[10px] sm:text-xs" style={{ color: S.textDim }}>{lang === 'is' ? 'Vinnustundir' : 'Work hours'}</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-lg sm:text-xl font-bold font-mono" style={{ color: S.green }}>{ISK(f.materialNet)}</p>
          <p className="text-[10px] sm:text-xs" style={{ color: S.textDim }}>
            {lang === 'is' ? 'Efni og kvittanir (án VSK)' : 'Materials and receipts (ex VAT)'}
          </p>
        </Card>
      </div>

      {isFixed ? (
        <>
          <Card className="p-3 sm:p-4">
            <h4 className="text-xs font-semibold mb-3" style={{ color: S.textMuted }}>{lang === 'is' ? 'Kostnaðaryfirlit' : 'Cost breakdown'}</h4>
            <div className="space-y-2 text-sm">
              <Row
                label={lang === 'is' ? 'Launakostnaður' : 'Labour cost'}
                sub={`${f.totalHours} ${lang === 'is' ? 'klst' : 'hrs'} x ${ISK(INTERNAL_RATE)} kr`}
                value={`${ISK(e.laborCost)} kr`}
              />
              <Row label={lang === 'is' ? 'Efniskostnaður (án VSK)' : 'Material cost (ex VAT)'} value={`${ISK(f.materialNet)} kr`} />
              {f.manualCosts > 0 && (
                <Row label={lang === 'is' ? 'Annar kostnaður' : 'Other costs'} value={`${ISK(f.manualCosts)} kr`} />
              )}
              <Row label={lang === 'is' ? 'Heildarkostnaður verkefnis' : 'Total project cost'} value={`${ISK(e.totalCost)} kr`} strong border />
            </div>
          </Card>

          <Card className="p-3 sm:p-4" data-testid="demo-finance-result">
            <h4 className="text-xs font-semibold mb-3" style={{ color: S.textMuted }}>{lang === 'is' ? 'Niðurstaða verks' : 'Project result'}</h4>
            <div className="space-y-2 text-sm">
              <Row label={lang === 'is' ? 'Fast verð (án VSK)' : 'Fixed price (ex VAT)'} value={`${ISK(project.fixed_price)} kr`} />
              <Row label={lang === 'is' ? 'Heildarkostnaður' : 'Total cost'} value={`-${ISK(e.totalCost)} kr`} color={S.red} />
              <Row
                label={lang === 'is' ? 'Framlegð' : 'Gross margin'}
                value={`${positive ? '+' : ''}${ISK(e.margin)} kr`}
                color={positive ? S.green : S.red}
                strong
                border
              />
              <p className="text-[10px] sm:text-xs text-right" style={{ color: S.textDim }}>
                {lang === 'is' ? 'Framlegðarhlutfall' : 'Margin'}: {e.marginPct}%
              </p>
            </div>
            {project.lesson && (
              <p className="text-[10px] sm:text-xs mt-3 pt-3" style={{ color: S.textMuted, borderTop: `1px solid ${S.border}` }}>
                {project.lesson[lang === 'is' ? 'is' : 'en']}
              </p>
            )}
          </Card>
        </>
      ) : (
        <Card className="p-3 sm:p-4" data-testid="demo-finance-billing">
          <h4 className="text-xs font-semibold mb-3" style={{ color: S.textMuted }}>{lang === 'is' ? 'Til reikningsfærslu' : 'Ready to invoice'}</h4>
          <div className="space-y-2 text-sm">
            <Row
              label={lang === 'is' ? 'Vinna' : 'Labour'}
              sub={`${f.totalHours} ${lang === 'is' ? 'klst' : 'hrs'} x ${ISK(project.hourly_rate)} kr`}
              value={`${ISK(f.totalHours * project.hourly_rate)} kr`}
            />
            <Row label={lang === 'is' ? 'Efni (án VSK)' : 'Materials (ex VAT)'} value={`${ISK(f.materialNet)} kr`} />
            <Row
              label={lang === 'is' ? 'Tilbúið til reikningsfærslu' : 'Ready to invoice'}
              value={`${ISK(e.billable)} kr`}
              color={S.green}
              strong
              border
            />
            <p className="text-[10px] sm:text-xs text-right" style={{ color: S.textDim }}>
              {lang === 'is' ? 'án VSK' : 'ex VAT'}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

// ── Project detail view ──
const ProjectDetailView = ({ project, onBack, lang }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: lang === 'is' ? 'Yfirlit' : 'Overview' },
    { id: 'receipts', label: lang === 'is' ? 'Kvittanir' : 'Receipts', count: project.receiptsCount },
    { id: 'hours', label: lang === 'is' ? 'Tímar' : 'Hours', count: project.hoursCount },
    { id: 'finance', label: lang === 'is' ? 'Fjármál' : 'Finance' },
  ];

  return (
    <div>
      <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full text-xs font-medium transition-colors hover:brightness-125"
            style={{ background: S.cardHover, color: S.textMuted }}
            data-testid="demo-back-btn"
          >
            <ArrowLeft size={14} />
            {lang === 'is' ? 'Öll verk' : 'All projects'}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-base font-semibold truncate">{project.name}</h2>
              <Badge variant={project.status === 'active' ? 'success' : 'neutral'}>
                {project.status === 'active' ? (lang === 'is' ? 'Virkt' : 'Active') : (lang === 'is' ? 'Lokið' : 'Finished')}
              </Badge>
            </div>
            <p className="text-[11px] sm:text-xs" style={{ color: S.textDim }}>{project.client}</p>
            <p className="text-[10px] font-mono mt-0.5" style={{ color: S.textMuted }}>
              {project.billing_type === 'fixed'
                ? `${lang === 'is' ? 'Fast verð (án VSK)' : 'Fixed price (ex VAT)'}: ${ISK(project.fixed_price)} kr`
                : `${lang === 'is' ? 'Tímagjald' : 'Hourly rate'}: ${ISK(project.hourly_rate)} kr/${lang === 'is' ? 'klst' : 'hr'}`}
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto px-3 sm:px-4" style={{ scrollbarWidth: 'none' }}>
        <div className="flex min-w-max" style={{ borderBottom: `1px solid ${S.border}` }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-3 sm:px-4 py-2.5 text-xs font-medium transition-colors whitespace-nowrap relative"
              style={{ color: activeTab === tab.id ? S.green : S.textMuted }}
              data-testid={`demo-tab-${tab.id}`}
            >
              {tab.label}
              {tab.count > 0 && <span className="ml-1" style={{ color: S.textDim }}>({tab.count})</span>}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: S.green }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ minHeight: '280px' }}>
        {activeTab === 'overview' && <OverviewTab project={project} lang={lang} />}
        {activeTab === 'receipts' && <ReceiptsTab project={project} lang={lang} />}
        {activeTab === 'hours' && <HoursTab project={project} lang={lang} />}
        {activeTab === 'finance' && <FinanceTab project={project} lang={lang} />}
      </div>
    </div>
  );
};

// ── Main demo component ──
const LandingDemo = ({ lang = 'is' }) => {
  DEMO_LANG = lang;
  const [selectedProject, setSelectedProject] = useState(null);
  const [touched, setTouched] = useState(false);

  const select = (p) => {
    setTouched(true);
    setSelectedProject(p);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden landing-fade"
      style={{ background: S.bg, border: `1px solid ${S.borderLight}` }}
      data-testid="landing-demo-widget"
    >
      <style>{`
        @keyframes demoPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.0); }
          50%      { box-shadow: 0 0 0 4px rgba(34,197,94,0.18); }
        }
        .demo-pulse { animation: demoPulse 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .demo-pulse { animation: none; } }
      `}</style>

      <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2" style={{ borderBottom: selectedProject ? 'none' : `1px solid ${S.border}` }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: S.green, color: S.bg }}>
            {DEMO_COMPANY.name.charAt(0)}
          </div>
          <span className="text-sm font-semibold truncate">{DEMO_COMPANY.name}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] px-2 py-0.5 rounded-full hidden sm:inline" style={{ background: S.greenSoft, color: S.green }}>
            {DEMO_COMPANY.employees} {lang === 'is' ? 'starfsmenn' : 'employees'}
          </span>
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
            style={{ background: 'rgba(34,197,94,0.14)', color: S.green, border: '1px solid rgba(34,197,94,0.3)' }}
            data-testid="demo-interactive-badge"
          >
            <MousePointerClick size={10} />
            {lang === 'is' ? 'Gagnvirkt sýnidæmi' : 'Interactive demo'}
          </span>
        </div>
      </div>

      {selectedProject ? (
        <ProjectDetailView
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
          lang={lang}
        />
      ) : (
        <ProjectListView onSelect={select} lang={lang} pulse={!touched} />
      )}
    </div>
  );
};

export default LandingDemo;
